import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton, Space, Typography } from "antd";

import { ApprovedResult } from "./ApprovedResult";
import { DeniedResult } from "./DeniedResult";
import { getAssistanceProgramApplicationItemsByAppId } from "../../../../../api/api";
import { useSelectedApplication } from "../../../hooks";
import ErrorMessage from "../../../../customComponent/customMessages/ErrorMessage";
import { DetailsSaved } from "./DetailsSaved";
import { getPhoneByProductName } from "./confirmationIdUtils";
import { getExpressEnrollmentMapping } from "../../../ApplicationFormEditor/configurations";
import { TEMPLATE_NAMES } from "../../../ApplicationFormEditor/templateNames";
import { SUPPORT_PROGRAM_PIDS } from "../../../../../utils/supportPrograms";
import { EnrollResultData } from "./EnrollResultData";
import { ReactComponent as CircledElectricityIcon } from "../../../../../assets/svg/circledElectricityIcon.svg";
import styled from "styled-components";
import { font14pxR } from "../../../../Typography";
import { GRAY_400 } from "../../../../../constant/colors";
import { useSelector } from "react-redux";
import { selectSelectedAssistanceProgramById } from "../../../../../store/selector";

const ITEM_ADMINISTRATION_TYPE = "administration";
const ITEM_STATUS = Object.freeze({
  APPROVED: "approved",
  REJECTED: "rejected"
});
const { Text } = Typography;

const TextStyled = styled(Text)`
  ${font14pxR}
  color: ${GRAY_400};
  display: flex;
`;

export const EnrollResult = ({
  id,
  hideDescription = false,
  titleStyle,
  applicationItemsWithConfirmationId,
  isPatientAlreadyEnrolled
}) => {
  const { t } = useTranslation();
  const application = useSelectedApplication();
  const [applicationItems, setApplicationItems] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const selectedAssistanceProgramById = useSelector(selectSelectedAssistanceProgramById);
  const phoneNumber = selectedAssistanceProgramById?.phone1;

  const patientAlreadyEnrolledText = getExpressEnrollmentMapping()?.patientAlreadyEnrolledText;

  const getItemNames = (items) => {
    const hasAdmin = items.some((item) => item.type === ITEM_ADMINISTRATION_TYPE);
    if (hasAdmin) {
      const drugName = items[0].rxnormDrug.bnName;
      return items.length === 1
        ? t("patientApplications.enroll.enroll_result.admin_only", { drug: drugName })
        : t("patientApplications.enroll.enroll_result.drug_and_administration", { drug: drugName });
    }

    const drugNames = items.map((item) => item.rxnormDrug.bnName);

    if (drugNames.length > 1) {
      return t("patientApplications.enroll.enroll_result.drugs", { drugs: drugNames.join(", ") });
    }

    return t("patientApplications.enroll.enroll_result.drug", { drug: drugNames[0] });
  };

  useEffect(() => {
    const fetchApplicationItems = async () => {
      setIsLoading(true);
      try {
        const applicationItems = await getAssistanceProgramApplicationItemsByAppId(application.id);
        if (applicationItems?.data) {
          setApplicationItems(applicationItems.data);
        } else {
          ErrorMessage(t("patientApplications.enroll.enroll_result.failed_to_get_submit_result"));
        }
      } catch (error) {
        ErrorMessage(t("patientApplications.enroll.enroll_result.failed_to_get_submit_result"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchApplicationItems();
  }, []);

  const approvedItems = [];
  let deniedItems = [];
  applicationItems?.forEach((item) => {
    if (item.status === ITEM_STATUS.APPROVED) {
      approvedItems.push(item);
    } else if (item.status === ITEM_STATUS.REJECTED) {
      deniedItems.push(item);
    }
  });

  if (applicationItemsWithConfirmationId) {
    deniedItems = deniedItems.map((deniedItem) => {
      const replacement =
        applicationItemsWithConfirmationId.find((item) => item.rxnormDrug.bnName === deniedItem.rxnormDrug.bnName) ||
        deniedItem;

      const phoneNumber = getPhoneByProductName(replacement.rxnormDrug.bnName.toLowerCase());

      return {
        ...replacement,
        phoneNumber
      };
    });
  }

  const programPid = application?.potentialSaving?.assistanceProgramPid;
  const templateName = application.ApplicationFormConfigurationTemplateName;
  const isTevaSupportProgram = SUPPORT_PROGRAM_PIDS.includes(programPid) && templateName === TEMPLATE_NAMES.TEVA_COPAY;

  const includeDrugName =
    ![TEMPLATE_NAMES.LILLY_COPAY, TEMPLATE_NAMES.LILLY_COPAY_SIGNATURE].includes(
      application.ApplicationFormConfigurationTemplateName
    ) && !isTevaSupportProgram;

  const approvedItemNames = getItemNames(approvedItems);
  const approvedTitle =
    approvedItems.length > 0 &&
    (includeDrugName
      ? t("patientApplications.enroll.enroll_result.application_approved_for", { items: approvedItemNames })
      : t("patientApplications.enroll.enroll_result.application_success_no_drugs"));

  const deniedItemsNames = getItemNames(deniedItems);
  const deniedTitle =
    deniedItems.length > 0 && deniedItems[0]?.confirmationId
      ? t("patientApplications.enroll.enroll_result.application_submitted_for", { items: deniedItemsNames })
      : t("patientApplications.enroll.enroll_result.application_denied_for", { items: deniedItemsNames });

  const shouldHideDetailsSaved = (deniedItems.length > 0 && deniedItems[0]?.confirmationId) || isPatientAlreadyEnrolled;
  return (
    <div id={id}>
      {isLoading ? (
        <Skeleton style={{ paddingTop: 16 }} active paragraph={{ rows: 10 }} />
      ) : (
        <Space
          size="large"
          style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
        >
          {isPatientAlreadyEnrolled && (
            <EnrollResultData
              icon={<CircledElectricityIcon />}
              title={t("patientApplications.enroll.enroll_result.copay_card_approved")}
              content={
                <TextStyled style={{ whiteSpace: "pre-line", textAlign: "center" }}>
                  {patientAlreadyEnrolledText(t, phoneNumber)}
                </TextStyled>
              }
              titleStyle={titleStyle}
            />
          )}
          {!!approvedItems?.length && (
            <ApprovedResult
              title={approvedTitle}
              applicationId={application?.id}
              hideDescription={hideDescription}
              titleStyle={titleStyle}
            />
          )}
          {!!deniedItems?.length && (
            <DeniedResult
              title={deniedTitle}
              denialReason={deniedItems[0].denialReason}
              confirmationId={deniedItems[0].confirmationId}
              phoneNumber={deniedItems[0].phoneNumber}
            />
          )}
          {!shouldHideDetailsSaved && <DetailsSaved />}
        </Space>
      )}
    </div>
  );
};
